import React, { useEffect, useRef, useState } from 'react';
import { api, getToken } from '../api.js';
import { useI18n } from '../i18n/index.jsx';
import { Modal } from '../pages/workspace/ws.jsx';
import { Alert } from './ui.jsx';

// Email signature editor (My mailbox › Signature, My signature — migration
// 039). Unlike the website / document editor (fixed classes, no inline
// styles), an email signature needs what email programs understand:
// tables for the layout, inline styles (font, size, colour, spacing), and
// pictures with an absolute address. So pasting a signature from Gmail,
// Outlook, Word or a website keeps its layout; the server keeps only safe
// HTML and copies outside pictures into our uploads (server: mailbox/
// signature.js). Built-in social icons, 3 ready-made layouts, a preview
// "as the customer will see it" and "Send me a test".

const SIZES = [['11px', 'small'], ['13px', 'normal'], ['16px', 'large'], ['20px', 'xl']];
const SOCIAL = ['whatsapp', 'facebook', 'youtube', 'linkedin', 'instagram'];
const FIELDS = ['name', 'title', 'company', 'phone', 'mobile', 'email', 'website', 'address'];
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const webUrl = (v) => (!v ? '' : /^https?:\/\//i.test(v) ? v : `https://${v}`);
const linkOf = (k, v) => {
  if (!v) return '';
  if (k === 'whatsapp') { const d = v.replace(/[^\d]/g, ''); return /^https?:/i.test(v) ? v : d ? `https://wa.me/${d}` : ''; }
  if (k === 'email') return `mailto:${v.trim()}`;
  if (k === 'phone' || k === 'mobile') return `tel:${v.replace(/[^\d+]/g, '')}`;
  return webUrl(v.trim());
};
const icon = (k, size = 22) => `<img src="/email-icons/${k}.png" width="${size}" height="${size}" alt="${k}" style="width:${size}px;height:${size}px;border:0;display:inline-block;vertical-align:middle">`;
const socialRow = (f, size = 22) => SOCIAL.filter((k) => f[k]).map((k) => `<a href="${esc(linkOf(k, f[k]))}" style="text-decoration:none">${icon(k, size)}</a>`).join('&nbsp;');

// The 3 ready-made layouts (tables + inline styles: same look in Gmail, Outlook, phones).
function layoutHtml(kind, f) {
  const acc = /^#[0-9a-f]{6}$/i.test(f.accent || '') ? f.accent : '#0b1f38';
  const font = 'font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.4;color:#1f2937';
  const lbl = (s) => `<span style="color:${acc};font-weight:bold">${s}</span>&nbsp;`;
  const a = (href, text) => `<a href="${esc(href)}" style="color:#1f2937;text-decoration:none">${esc(text)}</a>`;
  const who = `<strong style="font-size:15px;color:${acc}">${esc(f.name)}</strong>`;
  const role = [f.title, f.company].filter(Boolean).map(esc).join(' · ');
  const lines = [
    f.phone && `${lbl('T')}${a(linkOf('phone', f.phone), f.phone)}${f.mobile ? `&nbsp;&nbsp;${lbl('M')}${a(linkOf('mobile', f.mobile), f.mobile)}` : ''}`,
    !f.phone && f.mobile && `${lbl('M')}${a(linkOf('mobile', f.mobile), f.mobile)}`,
    f.email && `${lbl('E')}${a(linkOf('email', f.email), f.email)}`,
    f.website && `${lbl('W')}${a(webUrl(f.website), f.website.replace(/^https?:\/\//, ''))}`,
    f.address && `<span style="color:#64748b">${esc(f.address)}</span>`,
  ].filter(Boolean);
  const social = socialRow(f);
  if (kind === 'logo') {
    const logo = f.logo ? `<img src="${esc(f.logo)}" width="96" alt="${esc(f.company || 'logo')}" style="width:96px;border:0;display:block">` : '';
    return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;${font}"><tr>`
      + (logo ? `<td valign="middle" style="padding:0 14px 0 0;border-right:2px solid ${acc}">${logo}</td>` : '')
      + `<td valign="middle" style="padding:0 0 0 ${logo ? 14 : 0}px;${font}">${who}${role ? `<br><span style="color:#64748b">${role}</span>` : ''}<br>${lines.join('<br>')}${social ? `<br><span style="line-height:30px">${social}</span>` : ''}</td>`
      + '</tr></table>';
  }
  if (kind === 'simple') {
    return `<div style="${font}">${who}${role ? `<br><span style="color:#64748b">${role}</span>` : ''}<br>${lines.join('<br>')}</div>`;
  }
  // Contact icons + a row of social icons.
  const withIcon = (k, v, text) => (v ? `${icon(k, 16)}&nbsp;${a(k === 'website' ? webUrl(v) : linkOf(k, v), text || v)}` : '');
  const contact = [withIcon('phone', f.phone), withIcon('phone', f.mobile), withIcon('email', f.email), withIcon('website', f.website, (f.website || '').replace(/^https?:\/\//, ''))].filter(Boolean).join('&nbsp;&nbsp;&nbsp;');
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;${font}">`
    + `<tr><td style="padding:0 0 4px;${font}">${who}${role ? `<br><span style="color:#64748b">${role}</span>` : ''}</td></tr>`
    + (contact ? `<tr><td style="padding:4px 0;border-top:1px solid #e2e8f0;${font}">${contact}</td></tr>` : '')
    + (f.address ? `<tr><td style="padding:0 0 4px;color:#64748b;${font}">${esc(f.address)}</td></tr>` : '')
    + (social ? `<tr><td style="padding:6px 0 0">${socialRow(f, 26)}</td></tr>` : '')
    + '</table>';
}

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch('/api/mailbox/signature/image', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.error || 'server_error'), { code: j.error || 'server_error' });
  return j;
}

export default function SignatureEditor() {
  const { t, lang, errorText } = useI18n();
  const box = useRef(null);
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const [state, setState] = useState(null); // { html, compact, fields, connected, defaults }
  const [compact, setCompact] = useState(true);
  const [fields, setFields] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [img, setImg] = useState(null); // selected picture
  const [preview, setPreview] = useState(null);
  const [showLayouts, setShowLayouts] = useState(false);

  useEffect(() => {
    api('/mailbox/signature').then((r) => {
      setState(r); setCompact(r.compact);
      setFields({ ...r.defaults, ...r.fields });
      if (box.current) box.current.innerHTML = r.html || '';
      if (!r.html) setShowLayouts(true);
    }).catch((e) => setMsg({ kind: 'error', text: errorText(e) }));
  }, [errorText]);

  const html = () => box.current?.innerHTML || '';
  const changed = () => setDirty(true);
  const exec = (cmd, arg = null) => {
    box.current.focus();
    try { document.execCommand('styleWithCSS', false, true); } catch { /* older browsers */ }
    document.execCommand(cmd, false, arg);
    changed();
  };
  const fontSize = (px) => {
    exec('fontSize', '7');
    for (const el of box.current.querySelectorAll('font[size="7"]')) { const s = document.createElement('span'); s.style.fontSize = px; s.innerHTML = el.innerHTML; el.replaceWith(s); }
    for (const el of box.current.querySelectorAll('span[style*="xxx-large"]')) el.style.fontSize = px;
    changed();
  };
  const link = () => {
    const u = window.prompt(t('sigEmail.linkPrompt'), 'https://');
    if (!u || u === 'https://') return;
    const href = /^(https?:\/\/|mailto:|tel:)/i.test(u.trim()) ? u.trim() : /@/.test(u) ? `mailto:${u.trim()}` : `https://${u.trim()}`;
    exec('createLink', href);
  };
  const insertHtml = (h) => { box.current.focus(); document.execCommand('insertHTML', false, h); changed(); };
  const insertTable = () => insertHtml(`<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse"><tr><td valign="middle" style="padding:0 14px 0 0;width:110px">${t('sigEmail.logoCell')}</td><td valign="middle" style="padding:0 0 0 14px;border-left:2px solid #0b1f38">${t('sigEmail.textCell')}</td></tr></table><br>`);
  async function addPicture(file) {
    if (!file) return;
    setBusy('img');
    try { const r = await uploadImage(file); const w = r.width ? Math.min(r.width, 160) : 120; const h = r.width && r.height ? Math.round((w * r.height) / r.width) : ''; insertHtml(`<img src="${r.url}" width="${w}"${h ? ` height="${h}"` : ''} alt="" style="width:${w}px;${h ? `height:${h}px;` : ''}border:0">`); } catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(''); }
  }
  // Pasted signatures keep their layout; their pictures are copied to our site.
  async function onPaste(e) {
    const files = [...(e.clipboardData?.files || [])].filter((f) => f.type.startsWith('image/'));
    const pasted = e.clipboardData?.getData('text/html');
    if (!pasted && files.length) { e.preventDefault(); for (const f of files) await addPicture(f); return; }
    if (!pasted) return; // plain text: as typed
    e.preventDefault();
    const sel = window.getSelection(); const range = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    setBusy('paste');
    try {
      const r = await api('/mailbox/signature/clean', { method: 'POST', body: { html: pasted } });
      box.current.focus();
      if (range) { sel.removeAllRanges(); sel.addRange(range); }
      document.execCommand('insertHTML', false, r.html);
      changed();
    } catch (err) { setMsg({ kind: 'error', text: errorText(err) }); } finally { setBusy(''); }
  }
  // A picture clicked: its size.
  const pick = (e) => {
    if (e.target.tagName !== 'IMG') { setImg(null); return; }
    const r = e.target.getBoundingClientRect(); const b = box.current.getBoundingClientRect();
    setImg({ el: e.target, top: r.top - b.top + box.current.offsetTop - 42, left: Math.max(0, r.left - b.left) });
  };
  const resize = (w) => {
    const el = img?.el; if (!el) return;
    const ratio = el.naturalWidth ? el.naturalHeight / el.naturalWidth : (Number(el.getAttribute('height')) / Number(el.getAttribute('width')) || 1);
    const h = Math.round(w * ratio);
    el.setAttribute('width', String(w)); el.setAttribute('height', String(h)); el.style.width = `${w}px`; el.style.height = `${h}px`;
    changed(); setImg({ ...img });
  };
  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const useLayout = (kind) => {
    if (html().trim() && !window.confirm(t('sigEmail.replaceConfirm'))) return;
    box.current.innerHTML = layoutHtml(kind, fields);
    setImg(null); changed(); setShowLayouts(false);
  };
  async function save() {
    setBusy('save'); setMsg(null);
    try {
      const r = await api('/mailbox/signature', { method: 'PUT', body: { html: html(), compact, fields } });
      box.current.innerHTML = r.html || '';
      setImg(null); setDirty(false); setMsg({ kind: 'success', text: t('sigEmail.saved') });
    } catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(''); }
  }
  async function showPreview() {
    setBusy('preview');
    try { const r = await api('/mailbox/signature/preview', { method: 'POST', body: { html: html(), compact, lang } }); setPreview(r.email); } catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(''); }
  }
  async function sendTest() {
    setBusy('test'); setMsg(null);
    try { const r = await api('/mailbox/signature/test', { method: 'POST', body: { html: html(), compact, lang } }); setMsg({ kind: 'success', text: t('sigEmail.testSent', { email: r.sentTo }) }); } catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(''); }
  }
  const b = (key, label, onClick, extra = {}) => <button key={key} type="button" title={t(`sigEmail.tool.${key}`)} aria-label={t(`sigEmail.tool.${key}`)} onMouseDown={(e) => e.preventDefault()} onClick={onClick} {...extra}>{label}</button>;

  return (
    <div className="sigmail">
      {state && !state.connected && <p className="callout callout-info small">{t('sigEmail.notConnected')}</p>}
      <div className="sigmail-layouts">
        <button type="button" className="btn btn-ghost btn-sm" aria-expanded={showLayouts} onClick={() => setShowLayouts((x) => !x)}>✨ {t('sigEmail.layouts')} {showLayouts ? '▴' : '▾'}</button>
        {showLayouts && (
          <div className="sigmail-layout-panel">
            <p className="ws-hint">{t('sigEmail.layoutsHint')}</p>
            <div className="sigmail-fields">
              {FIELDS.map((k) => <label key={k} className="field"><span className="field-label">{t(`sigEmail.f.${k}`)}</span><input value={fields[k] || ''} onChange={(e) => setField(k, e.target.value)} /></label>)}
              {SOCIAL.map((k) => <label key={k} className="field"><span className="field-label"><img src={`/email-icons/${k}.png`} width="14" height="14" alt="" /> {t(`sigEmail.f.${k}`)}</span><input value={fields[k] || ''} onChange={(e) => setField(k, e.target.value)} placeholder={k === 'whatsapp' ? '+1 438 664 0000' : 'https://'} /></label>)}
              <label className="field"><span className="field-label">{t('sigEmail.f.accent')}</span><input type="color" value={fields.accent || '#0b1f38'} onChange={(e) => setField('accent', e.target.value)} /></label>
              <div className="field"><span className="field-label">{t('sigEmail.f.logo')}</span>
                <span className="sigmail-logo">{fields.logo && <img src={fields.logo} alt="" />}
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => logoRef.current?.click()}>{fields.logo ? t('sigEmail.logoChange') : t('sigEmail.logoAdd')}</button>
                  {fields.logo && <button type="button" className="btn btn-link btn-sm" onClick={() => setField('logo', '')}>✕</button>}</span>
                <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/gif" hidden onChange={async (e) => { const f = e.target.files[0]; e.target.value = ''; if (!f) return; try { setField('logo', (await uploadImage(f)).url); } catch (err) { setMsg({ kind: 'error', text: errorText(err) }); } }} /></div>
            </div>
            <div className="sigmail-layout-cards">
              {['logo', 'simple', 'social'].map((k) => (
                <button key={k} type="button" className="sigmail-card" onClick={() => useLayout(k)}>
                  <strong>{t(`sigEmail.layout.${k}`)}</strong>
                  <span className="sigmail-card-prev" dangerouslySetInnerHTML={{ __html: layoutHtml(k, fields) }} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="sigmail-bar" role="toolbar" aria-label={t('sigEmail.toolbar')}>
        {b('bold', <b>B</b>, () => exec('bold'))}
        {b('italic', <i>I</i>, () => exec('italic'))}
        {b('underline', <u>U</u>, () => exec('underline'))}
        <select aria-label={t('sigEmail.tool.size')} title={t('sigEmail.tool.size')} defaultValue="" onChange={(e) => { if (e.target.value) fontSize(e.target.value); e.target.value = ''; }}>
          <option value="">{t('sigEmail.tool.size')}</option>{SIZES.map(([px, k]) => <option key={k} value={px}>{t(`sigEmail.size.${k}`)}</option>)}
        </select>
        <label className="sigmail-color" title={t('sigEmail.tool.color')}><span aria-hidden="true">A</span><input type="color" aria-label={t('sigEmail.tool.color')} onChange={(e) => exec('foreColor', e.target.value)} /></label>
        {b('link', '🔗', link)}
        {b('br', '↵', () => exec('insertLineBreak'))}
        {b('image', '🖼', () => fileRef.current?.click())}
        {b('table', '▥', insertTable)}
        {b('alignLeft', '⇤', () => exec('justifyLeft'))}
        {b('alignCenter', '↔', () => exec('justifyCenter'))}
        {b('alignRight', '⇥', () => exec('justifyRight'))}
        <span className="sigmail-icons">
          {SOCIAL.concat(['website', 'phone', 'email']).map((k) => b(`icon_${k}`, <img src={`/email-icons/${k}.png`} width="18" height="18" alt="" />, () => {
            const url = k === 'phone' ? linkOf('phone', fields.phone || '') : k === 'email' ? linkOf('email', fields.email || '') : linkOf(k, fields[k] || '');
            const target = url || window.prompt(t('sigEmail.iconLinkPrompt'), k === 'whatsapp' ? 'https://wa.me/' : 'https://');
            if (target === null) return;
            insertHtml(`${target ? `<a href="${esc(target)}" style="text-decoration:none">` : ''}${icon(k)}${target ? '</a>' : ''}&nbsp;`);
          }, { title: t(`sigEmail.f.${k}`) }))}
        </span>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif" hidden onChange={(e) => { addPicture(e.target.files[0]); e.target.value = ''; }} />
      </div>
      <div className="sigmail-wrap">
        <div ref={box} className={`sigmail-area${compact ? ' is-compact' : ''}`} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label={t('sigEmail.title')}
          data-placeholder={t('sigEmail.placeholder')} onInput={changed} onPaste={onPaste} onClick={pick} />
        {img?.el?.isConnected && (
          <div className="sigmail-imgbar" style={{ top: Math.max(0, img.top), left: img.left }} role="toolbar" aria-label={t('sigEmail.tool.image')}>
            {[24, 32, 48, 64, 96, 128, 160, 200].map((w) => <button key={w} type="button" className={Number(img.el.getAttribute('width')) === w ? 'on' : ''} onMouseDown={(e) => e.preventDefault()} onClick={() => resize(w)}>{w}</button>)}
            <span>px</span>
            <button type="button" className="is-danger" onMouseDown={(e) => e.preventDefault()} onClick={() => { img.el.remove(); setImg(null); changed(); }} aria-label={t('sigEmail.removeImage')}>✕</button>
          </div>
        )}
        {busy === 'paste' && <p className="ws-hint">{t('sigEmail.pasting')}</p>}
      </div>
      <label className="field-checkbox sigmail-compact"><input type="checkbox" checked={compact} onChange={(e) => { setCompact(e.target.checked); changed(); }} /><span>{t('sigEmail.compact')}</span></label>
      {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
      <div className="ws-actions sigmail-actions">
        <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={showPreview}>👁 {t('sigEmail.preview')}</button>
        <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={sendTest}>✉ {busy === 'test' ? t('common.saving') : t('sigEmail.test')}</button>
        <button type="button" className="btn btn-primary" disabled={!!busy || !dirty} onClick={save}>{busy === 'save' ? t('common.saving') : t('sigEmail.save')}</button>
      </div>
      {preview && (
        <Modal title={t('sigEmail.previewTitle')} wide onClose={() => setPreview(null)}>
          <p className="ws-hint">{t('sigEmail.previewHint')}</p>
          <div className="sigmail-preview-tabs">
            <iframe title={t('sigEmail.previewTitle')} className="sigmail-frame" sandbox="" srcDoc={preview} />
            <iframe title={`${t('sigEmail.previewTitle')} — phone`} className="sigmail-frame is-phone" sandbox="" srcDoc={preview} />
          </div>
        </Modal>
      )}
    </div>
  );
}
